import { useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, Sparkles, Loader2, X } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

export const CardHistory = ({ token, palette }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [reflecting, setReflecting] = useState(false);
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && items === null) {
      try {
        const res = await axios.get(`${API}/lightdeck/history`, headers);
        setItems(res.data.items);
      } catch (e) {
        toast.error("Couldn't load your card journal");
        setItems([]);
      }
    }
  };

  const reflect = async () => {
    setReflecting(true);
    try {
      const res = await axios.post(`${API}/lightdeck/reflect`, { card_id: selected.card.id }, headers);
      setReflection(res.data.reflection);
    } catch (e) {
      toast.error("Spirit is resting — try again in a moment");
    } finally {
      setReflecting(false);
    }
  };

  const openCard = (item) => {
    setSelected(item);
    setReflection(null);
  };

  return (
    <div className="mt-4">
      <button
        onClick={toggle}
        className="w-full text-xs font-bold flex items-center justify-center gap-1.5"
        style={{ color: palette.primary }}
        data-testid="card-history-toggle"
      >
        <BookOpen className="w-4 h-4" />
        Your card journal
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-3 space-y-2" data-testid="card-history-list">
          {items === null && <p className="text-xs text-center opacity-60 py-2">Opening your journal…</p>}
          {items && items.length === 0 && (
            <p className="text-xs text-center opacity-60 py-2">No cards yet — your journal begins with your first draw 🌼</p>
          )}
          {items &&
            items.map((item) => (
              <button
                key={`${item.date}-${item.card.id}`}
                onClick={() => openCard(item)}
                className="w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors active:scale-[0.98]"
                style={{ borderColor: palette.border, backgroundColor: "#FFFFFF" }}
                data-testid={`card-history-item-${item.date}`}
              >
                <div className="w-9 h-12 rounded-md flex-shrink-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0F766E, #4A6B5D)" }}>
                  <Sparkles className="w-3.5 h-3.5" style={{ color: "#FCD34D" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: palette.text }}>{item.card.front}</p>
                  <p className="text-[10px] uppercase tracking-widest opacity-60">{item.card.suit}{item.bonus ? " · deeper draw" : ""}</p>
                </div>
                <span className="text-[11px] opacity-60 flex-shrink-0">{fmtDate(item.date)}</span>
              </button>
            ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)} data-testid="card-detail-modal">
          <div
            className="w-full max-w-md rounded-2xl bg-white overflow-y-auto"
            style={{ maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 text-center text-white relative" style={{ background: "linear-gradient(135deg, #0F766E, #4A6B5D)" }}>
              <button onClick={() => setSelected(null)} className="absolute top-3 right-3 p-1 rounded-full bg-white/15" aria-label="Close" data-testid="card-detail-close">
                <X className="w-4 h-4" />
              </button>
              <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FCD34D" }}>{selected.card.suit}</p>
              <h3 className="text-2xl font-bold my-1" style={{ fontFamily: "Georgia, serif" }}>{selected.card.front}</h3>
              <p className="text-xs opacity-85 italic">{selected.card.back}</p>
              <p className="text-[10px] mt-2 opacity-70">Found you on {fmtDate(selected.date)}</p>
            </div>
            <div className="p-5 space-y-4">
              {[
                ["The Meaning", selected.card.meaning],
                ["The Invitation", selected.card.invitation],
                ["To Sit With", selected.card.reflection],
              ].map(([label, text]) =>
                text ? (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: "#B08A3E" }}>{label}</p>
                    <p className="text-sm leading-relaxed" style={{ color: palette.text }}>{text}</p>
                  </div>
                ) : null
              )}

              {reflection ? (
                <div className="rounded-xl p-4" style={{ backgroundColor: `${palette.primary}0D`, border: `1px solid ${palette.primary}33` }} data-testid="card-ai-reflection">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: palette.primary }}>Spirit reflects</p>
                  <p className="text-sm leading-relaxed italic" style={{ color: palette.text }}>{reflection}</p>
                </div>
              ) : (
                <button
                  onClick={reflect}
                  disabled={reflecting}
                  className="w-full rounded-xl py-3 text-xs font-bold text-white flex items-center justify-center gap-2"
                  style={{ backgroundColor: palette.primary, opacity: reflecting ? 0.6 : 1 }}
                  data-testid="card-reflect-btn"
                >
                  {reflecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {reflecting ? "Spirit is reflecting…" : "Reflect on this card for me"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
